import { CommonMessage } from '@frontend/lang';
import { useAccountRepository } from '@frontend/repository';
import { TransactionPudoExtendedClient } from '@frontend/transaction/api';
import { ExtendedPudoTransaction, TransactionStateName } from '@frontend/transaction/types';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { isMovableTransactionState, useMoveTransaction } from '../../hooks/useMoveTransaction';
import { useKioskNavigate } from '../../utils/kiosk-navigation';

interface ViewProps {
    pudoTransaction: ExtendedPudoTransaction | null;
    loading: boolean;
    error: ReactNode | null;
    refetch: () => Promise<ExtendedPudoTransaction | null>;
    canMoveTransaction: boolean;
    navigateToMove: () => void;
}

const resolveSourceSlotId = (transaction: ExtendedPudoTransaction | null): string | null => transaction?.slot?.entity_id ?? transaction?.slot_id ?? null;

const usePudoTransactionDetail = (): ViewProps => {
    const { id } = useParams<{ id: string }>();
    const { currentAccount } = useAccountRepository();
    const navigate = useKioskNavigate();
    const { canMove } = useMoveTransaction();
    const [pudoTransaction, setPudoTransaction] = useState<ExtendedPudoTransaction | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<ReactNode | null>(null);

    const fetchPudoTransaction = useCallback(async (): Promise<ExtendedPudoTransaction | null> => {
        if (!id || !currentAccount) return null;

        try {
            setLoading(true);
            setError(null);
            const transaction = await TransactionPudoExtendedClient.fetchPudoExtendedTransaction(currentAccount, id);
            transaction ? setPudoTransaction(transaction) : setError(CommonMessage.ERRORS.ENTITY_NOT_FOUND(CommonMessage.OBJECTS.TRANSACTION.DEFAULT));
            return transaction;
        } catch {
            setError(CommonMessage.ERRORS.FAILED_TO_LOAD);
            return null;
        } finally {
            setLoading(false);
        }
    }, [id, currentAccount]);

    useEffect(() => {
        void fetchPudoTransaction();
    }, [fetchPudoTransaction]);

    const sourceSlotId = resolveSourceSlotId(pudoTransaction);
    const canMoveTransaction = !!pudoTransaction && !!sourceSlotId && canMove && isMovableTransactionState(pudoTransaction.state as TransactionStateName);

    const navigateToMove = useCallback(() => {
        if (!pudoTransaction || !sourceSlotId) return;
        navigate(`/spots/layout/slot/${sourceSlotId}/move/${pudoTransaction.id}`);
    }, [navigate, pudoTransaction, sourceSlotId]);

    return {
        pudoTransaction,
        loading,
        error,
        refetch: fetchPudoTransaction,
        canMoveTransaction,
        navigateToMove
    };
};

export default usePudoTransactionDetail;
